import type {
  CalendarCell,
  CalendarDate,
  CalendarDateRange,
  DateControlLocale,
  YearMonth,
  YearQuarter,
} from './types';

export const monthLabels = {
  ko: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

export const weekdayLabels = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

export const toDate = (value: CalendarDate) =>
  new Date(value.year, value.month - 1, value.day);

export const fromDate = (value: Date): CalendarDate => ({
  year: value.getFullYear(),
  month: value.getMonth() + 1,
  day: value.getDate(),
});

export const createOptionalCalendarDate = (
  year?: number,
  month?: number,
  day?: number,
): CalendarDate | null => {
  if (
    typeof year !== 'number' ||
    typeof month !== 'number' ||
    typeof day !== 'number'
  ) {
    return null;
  }

  return {
    year,
    month,
    day,
  };
};

export const getToday = () => fromDate(new Date());

export const getCurrentQuarter = () => Math.ceil(getToday().month / 3);

export const addDays = (value: CalendarDate, amount: number) => {
  const date = toDate(value);
  date.setDate(date.getDate() + amount);
  return fromDate(date);
};

export const addMonths = (value: YearMonth, amount: number): YearMonth => {
  const date = new Date(value.year, value.month - 1 + amount, 1);

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  };
};

export const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

export const compareDate = (left: CalendarDate, right: CalendarDate) =>
  toDate(left).getTime() - toDate(right).getTime();

export const compareMonth = (left: YearMonth, right: YearMonth) =>
  left.year === right.year ? left.month - right.month : left.year - right.year;

export const calendarDateToYearMonth = (value: CalendarDate): YearMonth => ({
  year: value.year,
  month: value.month,
});

export const getEarlierDate = (left: CalendarDate, right: CalendarDate) =>
  compareDate(left, right) <= 0 ? left : right;

export const isFutureDate = (value: CalendarDate) => compareDate(value, getToday()) > 0;

export const isFutureMonth = (value: YearMonth) => {
  const today = getToday();

  return value.year > today.year || (value.year === today.year && value.month > today.month);
};

export const isFutureQuarter = (value: YearQuarter) => {
  const today = getToday();
  const currentQuarter = getCurrentQuarter();

  return (
    value.year > today.year ||
    (value.year === today.year && value.quarter > currentQuarter)
  );
};

export const isFutureYear = (value: number) => value > getToday().year;

export const clampMonthToToday = (value: YearMonth) => {
  if (!isFutureMonth(value)) {
    return value;
  }

  const today = getToday();

  return {
    year: today.year,
    month: today.month,
  };
};

export const clampQuarterToToday = (value: YearQuarter) => {
  if (!isFutureQuarter(value)) {
    return value;
  }

  const today = getToday();

  return {
    year: today.year,
    quarter: getCurrentQuarter(),
  };
};

export const isSameDate = (left: CalendarDate | null, right: CalendarDate | null) =>
  Boolean(
    left &&
      right &&
      left.year === right.year &&
      left.month === right.month &&
      left.day === right.day,
  );

export const isWithinRange = (date: CalendarDate, range?: CalendarDateRange) => {
  if (!range?.start || !range.end) {
    return false;
  }

  return compareDate(date, range.start) >= 0 && compareDate(date, range.end) <= 0;
};

export const getCalendarCells = (visibleMonth: YearMonth): CalendarCell[] => {
  const firstDay = new Date(visibleMonth.year, visibleMonth.month - 1, 1).getDay();
  const currentMonthDays = getDaysInMonth(visibleMonth.year, visibleMonth.month);
  const previousMonth = addMonths(visibleMonth, -1);
  const previousMonthDays = getDaysInMonth(previousMonth.year, previousMonth.month);
  const cells: CalendarCell[] = [];

  for (let index = firstDay - 1; index >= 0; index -= 1) {
    cells.push({
      date: {
        year: previousMonth.year,
        month: previousMonth.month,
        day: previousMonthDays - index,
      },
      isCurrentMonth: false,
    });
  }

  for (let day = 1; day <= currentMonthDays; day += 1) {
    cells.push({
      date: {
        year: visibleMonth.year,
        month: visibleMonth.month,
        day,
      },
      isCurrentMonth: true,
    });
  }

  const nextMonth = addMonths(visibleMonth, 1);
  let nextDay = 1;

  while (cells.length < 42) {
    cells.push({
      date: {
        year: nextMonth.year,
        month: nextMonth.month,
        day: nextDay,
      },
      isCurrentMonth: false,
    });
    nextDay += 1;
  }

  return cells;
};

export const formatMonth = (value: YearMonth, locale: DateControlLocale) =>
  locale === 'ko'
    ? `${value.year}년 ${value.month}월`
    : `${monthLabels.en[value.month - 1]} ${value.year}`;

export const formatQuarter = (value: YearQuarter, locale: DateControlLocale) =>
  locale === 'ko' ? `${value.year}년 ${value.quarter}분기` : `Q${value.quarter} ${value.year}`;

export const formatYear = (value: number, locale: DateControlLocale) =>
  locale === 'ko' ? `${value}년` : String(value);

export const formatCalendarDate = (value: CalendarDate, locale: DateControlLocale) =>
  locale === 'ko'
    ? `${value.year}년 ${value.month}월 ${value.day}일`
    : `${monthLabels.en[value.month - 1]} ${value.day}, ${value.year}`;

export const formatOptionalCalendarDate = (
  value: CalendarDate | null,
  locale: DateControlLocale,
) => (value ? formatCalendarDate(value, locale) : locale === 'ko' ? '날짜 선택' : 'Select date');

export const getYearGroupStart = (year: number) => Math.floor(year / 10) * 10;

export const getRangeDays = (range: CalendarDateRange) => {
  if (!range.start || !range.end) {
    return 0;
  }

  return Math.floor((toDate(range.end).getTime() - toDate(range.start).getTime()) / 86400000) + 1;
};
